import { useState, useCallback, useEffect, type FormEvent } from 'react';
import { apiClient, ApiClientError } from '../services/api-client';
import { recordOptionOutcome } from '../services/trade-service';
import {
  validateOptionOutcomeInput,
  selectSchedule,
  computeCommission,
  computeExerciseFee,
  OptionOutcome,
  InstrumentClass,
  type ValidationError,
} from '@covered-calls/shared';
import type {
  BrokerProfile,
  CurrentOption,
  OptionOutcomeInput,
  CommissionResult,
  Exchange,
} from '@covered-calls/shared';

/** Contracts are derived from share quantity (ASX/IBKR 100-share contracts). */
const OPTION_CONTRACT_MULTIPLIER = 100;

/**
 * OptionOutcomeDialog — record the end-of-life outcome of an active covered
 * call. Mirrors `PutOutcomeDialog`.
 *
 * Offers two outcomes:
 * - EXPIRED (expired worthless): shares kept, full premium retained, position
 *   stays open. No extra fields.
 * - ASSIGNED (called away): shares sold at the option's strike and the position
 *   closes. Captures an editable assignment date (defaulting to the option's
 *   expiry) and a stock-sale commission (SHARES schedule, `strike × quantity`),
 *   with a commission/GST preview, plus a preview of the broker's additive
 *   exercise/assignment fee (OPTIONS schedule, `quantity / 100` contracts) that
 *   the backend resolves automatically.
 *
 * Submits an OptionOutcomeInput to the option-outcome endpoint.
 *
 * Validates: Requirements 1.3, 1.4, 1.5, 1.6, 2.1, 2.2, 5.1, 7.4
 */

export interface OptionOutcomeDialogProps {
  positionId: string;
  /** The exchange the underlying trades on; keys the commission schedule selection. */
  exchange: Exchange;
  /** Share quantity — drives the sale trade value and the contract count. */
  quantity: number;
  /** The active covered call being recorded (for display and context). */
  currentOption: CurrentOption;
  /** Optional pre-loaded broker profiles; fetched on mount when omitted. */
  brokerProfiles?: BrokerProfile[];
  /**
   * When provided, the dialog pre-selects this outcome and HIDES its internal
   * outcome selector (the outcome has already been chosen upstream). When
   * omitted, behaviour is exactly as today: the selector is shown and defaults
   * to EXPIRED. Additive and backwards compatible.
   */
  forcedOutcome?: OptionOutcome;
  onSuccess: () => void;
  onCancel: () => void;
}

interface FormState {
  outcome: OptionOutcome;
  /** For ASSIGNED, defaults to the option's expiry; editable. */
  outcomeDate: string;
  /** Stock-sale commission override for the strike-price sale (ASSIGNED). */
  saleCommission: string;
  brokerProfileId: string;
}

/** The two outcomes this dialog offers, in display order. */
const OFFERED_OUTCOMES: OptionOutcome[] = [OptionOutcome.EXPIRED, OptionOutcome.ASSIGNED];

/** Human-readable labels for the offered outcomes. */
const OUTCOME_LABELS: Record<string, string> = {
  [OptionOutcome.EXPIRED]: 'Expired worthless',
  [OptionOutcome.ASSIGNED]: 'Assigned (called away)',
};

export function OptionOutcomeDialog({
  positionId,
  exchange,
  quantity,
  currentOption,
  brokerProfiles: initialProfiles,
  forcedOutcome,
  onSuccess,
  onCancel,
}: OptionOutcomeDialogProps) {
  const contracts = quantity / OPTION_CONTRACT_MULTIPLIER;

  const initialFormState: FormState = {
    // When an upstream chooser has already decided the outcome, pre-select it
    // (and the internal selector is hidden below). Otherwise default to EXPIRED.
    outcome: forcedOutcome ?? OptionOutcome.EXPIRED,
    // ASSIGNED defaults the assignment date to the option's expiry (Req 2.1),
    // editable (Req 2.2). Pre-populated now so switching to ASSIGNED shows it.
    outcomeDate: currentOption.expiryDate,
    saleCommission: '',
    brokerProfileId: '',
  };

  const [form, setForm] = useState<FormState>(initialFormState);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [serverError, setServerError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [brokerProfiles, setBrokerProfiles] = useState<BrokerProfile[]>(initialProfiles ?? []);
  // Commission/GST split for the stock sale at the strike (SHARES schedule).
  const [commissionBreakdown, setCommissionBreakdown] = useState<CommissionResult | null>(null);
  // Preview of the additive option exercise/assignment fee (OPTIONS schedule).
  const [exerciseFeeBreakdown, setExerciseFeeBreakdown] = useState<CommissionResult | null>(null);
  // True when a profile is selected but has no SHARES schedule for this exchange.
  const [noScheduleForLeg, setNoScheduleForLeg] = useState(false);

  const isAssigned = form.outcome === OptionOutcome.ASSIGNED;
  const strike = currentOption.strikePrice;

  // Fetch broker profiles on mount when not supplied by the parent.
  useEffect(() => {
    if (initialProfiles && initialProfiles.length > 0) {
      return;
    }
    async function loadProfiles() {
      try {
        const profiles = await apiClient.get<BrokerProfile[]>('/broker-profiles');
        setBrokerProfiles(profiles);
      } catch {
        // Non-critical: continue without profiles
      }
    }
    loadProfiles();
  }, [initialProfiles]);

  /**
   * Resolve the assignment previews from the selected broker profile:
   * - the stock-sale commission via the (exchange, SHARES) schedule using
   *   tradeValue = strike × quantity, populating the commission field with the
   *   effective `total` and recording the split for the preview; and
   * - the additive option exercise/assignment fee via the (exchange, OPTIONS)
   *   schedule using the derived contract count, shown as an informational
   *   preview (the backend resolves and applies it).
   *
   * When the profile has no SHARES schedule for this exchange, falls back to
   * manual entry (does not crash).
   */
  const resolveProfile = useCallback(
    (profileId: string) => {
      if (!profileId) {
        setCommissionBreakdown(null);
        setExerciseFeeBreakdown(null);
        setNoScheduleForLeg(false);
        return;
      }

      const profile = brokerProfiles.find((p) => p.profileId === profileId);
      if (!profile) {
        setCommissionBreakdown(null);
        setExerciseFeeBreakdown(null);
        setNoScheduleForLeg(false);
        return;
      }

      // Additive option exercise/assignment fee preview (OPTIONS schedule).
      const optionsSchedule = selectSchedule(profile, exchange, InstrumentClass.OPTIONS);
      if (optionsSchedule) {
        const fee = computeExerciseFee(optionsSchedule, contracts);
        setExerciseFeeBreakdown(fee.total > 0 ? fee : null);
      } else {
        setExerciseFeeBreakdown(null);
      }

      // Stock-sale commission on the strike-price sale (SHARES schedule).
      const sharesSchedule = selectSchedule(profile, exchange, InstrumentClass.SHARES);
      if (!sharesSchedule) {
        // No schedule for the stock leg — fall back to manual entry.
        setCommissionBreakdown(null);
        setNoScheduleForLeg(true);
        return;
      }

      const tradeValue = strike * quantity;
      const breakdown = computeCommission(sharesSchedule, { tradeValue, quantity });

      setNoScheduleForLeg(false);
      setCommissionBreakdown(breakdown);
      setForm((prev) => ({ ...prev, saleCommission: breakdown.total.toFixed(2) }));
    },
    [brokerProfiles, exchange, quantity, strike, contracts]
  );

  const handleFieldChange = useCallback(
    (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const value = e.target.value;
      setForm((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => prev.filter((err) => err.field !== field));
      setServerError(null);
    },
    []
  );

  // Changing the outcome clears any assignment-specific preview.
  const handleOutcomeChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const outcome = e.target.value as OptionOutcome;
      setForm((prev) => ({ ...prev, outcome }));
      setErrors([]);
      setServerError(null);
      if (outcome !== OptionOutcome.ASSIGNED) {
        setCommissionBreakdown(null);
        setExerciseFeeBreakdown(null);
        setNoScheduleForLeg(false);
      }
    },
    []
  );

  // Auto-populate the stock-sale commission (and fee preview) on profile change.
  const handleBrokerProfileChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const profileId = e.target.value;
      setForm((prev) => ({ ...prev, brokerProfileId: profileId }));
      resolveProfile(profileId);
      setServerError(null);
    },
    [resolveProfile]
  );

  const getFieldError = (field: string): string | undefined => {
    return errors.find((err) => err.field === field)?.message;
  };

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setServerError(null);

      const input: OptionOutcomeInput = {
        outcome: form.outcome,
        outcomeDate: form.outcomeDate,
        brokerProfileId: form.brokerProfileId || undefined,
      };

      // The stock-sale commission override only applies to an assignment; the
      // expired-worthless path carries no sale.
      if (form.outcome === OptionOutcome.ASSIGNED && form.saleCommission.trim() !== '') {
        input.saleCommission = parseFloat(form.saleCommission);
      }

      // Client-side validation
      const validationErrors = validateOptionOutcomeInput(input);

      if (validationErrors.length > 0) {
        setErrors(validationErrors);
        return;
      }

      setErrors([]);
      setIsSubmitting(true);

      try {
        await recordOptionOutcome(positionId, input);
        onSuccess();
      } catch (error) {
        if (error instanceof ApiClientError) {
          if (error.errors && error.errors.length > 0) {
            setErrors(error.errors.map((err) => ({ field: err.field, message: err.message })));
          } else {
            setServerError(error.message || 'An error occurred. Please try again.');
          }
        } else {
          setServerError('An unexpected error occurred. Please try again.');
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [form, positionId, onSuccess]
  );

  return (
    <div
      role="dialog"
      aria-labelledby="option-outcome-dialog-title"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1000,
        padding: '1rem',
      }}
    >
      <div
        style={{
          backgroundColor: 'var(--color-surface)',
          borderRadius: '8px',
          padding: '2rem',
          maxWidth: '560px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.15)',
        }}
      >
        <h2 id="option-outcome-dialog-title" style={{ marginTop: 0, marginBottom: '0.5rem' }}>
          Record Option Outcome
        </h2>
        <p style={{ color: 'var(--color-muted)', marginBottom: '1.5rem', fontSize: '0.875rem' }}>
          Covered call — strike ${currentOption.strikePrice.toFixed(2)}, premium (received) $
          {currentOption.premium.toFixed(2)}/share, expiry {currentOption.expiryDate}.
        </p>

        {serverError && (
          <div
            role="alert"
            style={{
              backgroundColor: 'var(--color-chip-danger-bg)',
              color: 'var(--color-chip-danger-text)',
              padding: '0.75rem 1rem',
              borderRadius: '4px',
              marginBottom: '1rem',
              fontSize: '0.875rem',
            }}
          >
            {serverError}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          {/* Outcome Selector — hidden when an upstream chooser has already
              fixed the outcome (`forcedOutcome`), to avoid a double selector
              (Req 2.3). When omitted, shown exactly as today. */}
          {forcedOutcome === undefined && (
            <div style={{ marginBottom: '1rem' }}>
              <label
                htmlFor="option-outcome"
                style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.25rem', fontWeight: 500 }}
              >
                Outcome
                <OptionOutcomeInfoButton />
              </label>
              <select
                id="option-outcome"
                value={form.outcome}
                onChange={handleOutcomeChange}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid var(--color-input-border)',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  boxSizing: 'border-box',
                  backgroundColor: 'var(--color-surface)',
                }}
              >
                {OFFERED_OUTCOMES.map((outcome) => (
                  <option key={outcome} value={outcome}>
                    {OUTCOME_LABELS[outcome]}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Expired-worthless description — no extra fields (Req 1.4) */}
          {!isAssigned && (
            <div
              role="status"
              style={{
                backgroundColor: 'var(--color-surface-2)',
                color: 'var(--color-muted)',
                padding: '0.75rem 1rem',
                borderRadius: '4px',
                marginBottom: '1.5rem',
                fontSize: '0.8125rem',
                lineHeight: '1.5',
              }}
            >
              <strong style={{ color: 'var(--color-text)' }}>Expired worthless:</strong> the call
              lapsed out-of-the-money. You keep your {quantity.toLocaleString()} shares, retain the
              full premium of ${currentOption.premium.toFixed(2)}/share, and the position stays open
              so you can sell a new option against the same holding.
            </div>
          )}

          {/* Assignment fields — shown for the ASSIGNED outcome (Req 1.5). The
              call is assigned: the shares are sold at the strike and the whole
              position closes. */}
          {isAssigned && (
            <>
              <div
                role="status"
                style={{
                  backgroundColor: 'var(--color-brand-fill)',
                  color: 'var(--color-link)',
                  padding: '0.75rem 1rem',
                  borderRadius: '4px',
                  marginBottom: '1rem',
                  fontSize: '0.8125rem',
                  lineHeight: '1.5',
                }}
              >
                <strong>Assigned (called away):</strong> your{' '}
                {quantity.toLocaleString()} shares are sold at the strike price of{' '}
                <strong>${strike.toFixed(2)}</strong> and this position will be closed. The broker's
                option exercise/assignment fee applies in addition to the stock-sale commission.
              </div>

              {/* Assignment Date — defaults to the option expiry (Req 2.1), editable (Req 2.2) */}
              <div style={{ marginBottom: '1rem' }}>
                <label
                  htmlFor="option-outcome-date"
                  style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}
                >
                  Assignment Date
                </label>
                <input
                  id="option-outcome-date"
                  type="date"
                  value={form.outcomeDate}
                  onChange={handleFieldChange('outcomeDate')}
                  aria-invalid={!!getFieldError('outcomeDate')}
                  aria-describedby={getFieldError('outcomeDate') ? 'option-outcome-date-error' : undefined}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: `1px solid ${getFieldError('outcomeDate') ? 'var(--color-danger)' : 'var(--color-input-border)'}`,
                    borderRadius: '4px',
                    fontSize: '1rem',
                    boxSizing: 'border-box',
                  }}
                />
                {getFieldError('outcomeDate') ? (
                  <span id="option-outcome-date-error" style={{ color: 'var(--color-danger)', fontSize: '0.75rem' }}>
                    {getFieldError('outcomeDate')}
                  </span>
                ) : (
                  <span style={{ fontSize: '0.75rem', color: 'var(--color-muted)' }}>
                    Defaults to the option expiry ({currentOption.expiryDate}). Change it if the
                    shares were called away on a different date.
                  </span>
                )}
              </div>

              {/* Broker Profile — selecting one auto-resolves the stock-sale
                  commission (SHARES schedule) and previews the additive option
                  exercise/assignment fee (OPTIONS schedule). */}
              <div style={{ marginBottom: '1rem' }}>
                <label
                  htmlFor="option-broker-profile"
                  style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}
                >
                  Broker Profile (optional)
                </label>
                <select
                  id="option-broker-profile"
                  value={form.brokerProfileId}
                  onChange={handleBrokerProfileChange}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid var(--color-input-border)',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    boxSizing: 'border-box',
                    backgroundColor: 'var(--color-surface)',
                  }}
                >
                  <option value="">-- Select broker profile --</option>
                  {brokerProfiles.map((profile) => (
                    <option key={profile.profileId} value={profile.profileId}>
                      {profile.brokerName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Exercise/assignment fee preview — informational; the backend
                  resolves and applies this additive fee from the OPTIONS
                  schedule. Only shown when the selected profile defines one. */}
              {exerciseFeeBreakdown && (
                <div
                  data-testid="option-exercise-fee-breakdown"
                  style={{
                    marginBottom: '1rem',
                    padding: '0.5rem 0.75rem',
                    backgroundColor: 'var(--color-surface-2)',
                    borderRadius: '4px',
                    fontSize: '0.8125rem',
                    color: 'var(--color-muted)',
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: '0.25rem', color: 'var(--color-text)' }}>
                    Option exercise/assignment fee ({contracts} contract{contracts === 1 ? '' : 's'})
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Fee</span>
                    <span>${exerciseFeeBreakdown.commission.toFixed(2)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>GST</span>
                    <span>${exerciseFeeBreakdown.gst.toFixed(2)}</span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontWeight: 600,
                      borderTop: '1px solid var(--color-border)',
                      marginTop: '0.25rem',
                      paddingTop: '0.25rem',
                    }}
                  >
                    <span>Total</span>
                    <span>${exerciseFeeBreakdown.total.toFixed(2)}</span>
                  </div>
                  <span style={{ display: 'block', fontSize: '0.72rem', marginTop: '0.25rem' }}>
                    Applied automatically on top of the stock-sale commission.
                  </span>
                </div>
              )}

              {/* Stock-Sale Commission — override for the strike-price sale,
                  auto-populated from the SHARES schedule. */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label
                  htmlFor="option-sale-commission"
                  style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}
                >
                  Stock-Sale Commission ($)
                </label>
                <input
                  id="option-sale-commission"
                  type="number"
                  step="0.01"
                  min="0.00"
                  max="99999.99"
                  value={form.saleCommission}
                  onChange={handleFieldChange('saleCommission')}
                  placeholder="0.00"
                  aria-invalid={!!getFieldError('saleCommission')}
                  aria-describedby={
                    getFieldError('saleCommission') ? 'option-sale-commission-error' : undefined
                  }
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: `1px solid ${getFieldError('saleCommission') ? 'var(--color-danger)' : 'var(--color-input-border)'}`,
                    borderRadius: '4px',
                    fontSize: '1rem',
                    boxSizing: 'border-box',
                  }}
                />
                {getFieldError('saleCommission') && (
                  <span id="option-sale-commission-error" style={{ color: 'var(--color-danger)', fontSize: '0.75rem' }}>
                    {getFieldError('saleCommission')}
                  </span>
                )}

                {/* Commission / GST split from the shared calculator (SHARES) */}
                {commissionBreakdown && (
                  <div
                    data-testid="option-sale-commission-breakdown"
                    style={{
                      marginTop: '0.5rem',
                      padding: '0.5rem 0.75rem',
                      backgroundColor: 'var(--color-surface-2)',
                      borderRadius: '4px',
                      fontSize: '0.8125rem',
                      color: 'var(--color-muted)',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Commission</span>
                      <span>${commissionBreakdown.commission.toFixed(2)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>GST</span>
                      <span>${commissionBreakdown.gst.toFixed(2)}</span>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontWeight: 600,
                        borderTop: '1px solid var(--color-border)',
                        marginTop: '0.25rem',
                        paddingTop: '0.25rem',
                      }}
                    >
                      <span>Total</span>
                      <span>${commissionBreakdown.total.toFixed(2)}</span>
                    </div>
                  </div>
                )}

                {/* No SHARES schedule for this exchange — manual entry */}
                {noScheduleForLeg && (
                  <span style={{ display: 'block', fontSize: '0.75rem', color: 'var(--color-chip-warning-text)', marginTop: '0.25rem' }}>
                    This broker profile has no schedule for {exchange} shares. Enter the commission manually.
                  </span>
                )}

                <span style={{ fontSize: '0.75rem', color: 'var(--color-muted)' }}>
                  The commission on selling your shares at the strike. Auto-populated from the
                  selected broker profile; override if your trade report differs. The option
                  exercise/assignment fee above is applied separately.
                </span>
              </div>
            </>
          )}

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: 'var(--color-muted)',
                color: 'var(--color-on-brand)',
                border: 'none',
                borderRadius: '4px',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                minHeight: '44px',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: 'var(--color-brand)',
                color: 'var(--color-on-brand)',
                border: 'none',
                borderRadius: '4px',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                minHeight: '44px',
                opacity: isSubmitting ? 0.7 : 1,
              }}
            >
              {isSubmitting ? 'Recording...' : 'Record Outcome'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Info button with a popup explaining how to choose the correct covered-call
 * outcome. Mirrors the PutOutcomeInfoButton pattern: an "i" toggle that reveals
 * a tooltip, with a full-screen click-catcher to dismiss. Reinforces that a
 * worthless expiry and an assignment are different events (Req 1.6).
 */
function OptionOutcomeInfoButton() {
  const [showInfo, setShowInfo] = useState(false);

  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={() => setShowInfo((v) => !v)}
        aria-label="How do I choose the right outcome?"
        aria-expanded={showInfo}
        style={{
          background: 'none',
          border: '1px solid var(--color-muted)',
          borderRadius: '50%',
          width: '18px',
          height: '18px',
          minHeight: '18px',
          fontSize: '0.65rem',
          fontWeight: 700,
          color: 'var(--color-muted)',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
        }}
      >
        i
      </button>
      {showInfo && (
        <>
          <div
            onClick={() => setShowInfo(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 1099 }}
          />
          <div
            role="tooltip"
            style={{
              position: 'absolute',
              top: '24px',
              left: '0',
              backgroundColor: 'var(--color-header-bg)',
              color: 'var(--color-header-text)',
              borderRadius: '8px',
              padding: '1rem',
              fontSize: '0.8rem',
              lineHeight: '1.5',
              width: '320px',
              zIndex: 1100,
              boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
              fontWeight: 400,
            }}
          >
            <p style={{ fontWeight: 600, margin: '0 0 0.5rem', fontSize: '0.85rem' }}>Which outcome do I pick?</p>
            <div style={{ marginBottom: '0.5rem' }}>
              <span style={{ fontWeight: 600 }}>Expired worthless</span>
              <span> — the call finished out-of-the-money and lapsed. You keep your shares and the full premium, and the position stays open. Nothing is sold.</span>
            </div>
            <div style={{ marginBottom: '0.5rem' }}>
              <span style={{ fontWeight: 600 }}>Assigned (called away)</span>
              <span> — the call finished in-the-money and the counterparty exercised, so your shares were sold at the strike price. This closes the whole position and records the sale, realised P&amp;L and a CGT event at the strike.</span>
            </div>
            <p style={{ margin: '0.5rem 0 0', fontSize: '0.72rem', color: 'var(--color-header-muted)' }}>
              Tip: these are opposite outcomes — only pick "Assigned" if your shares were actually sold at the strike. If you still hold the shares, the call expired worthless.
            </p>
            <p style={{ margin: '0.5rem 0 0', fontSize: '0.72rem', color: 'var(--color-header-muted)' }}>
              Tap anywhere to close
            </p>
          </div>
        </>
      )}
    </span>
  );
}
